import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { NetworkRequest } from "../../../../common/models";
import {
  MutationOrNetworkState,
  NetworkRequestState,
} from "../../../../common/models/NetworkRequest";
import {
  OrganizationDataRecipientDto,
  ProcessingRegion as ProcessingRegionEnum,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { Loading } from "../../../components";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { useOrganizationVendors, usePrimaryOrganization } from "../../../hooks";
import { useUpdateVendor } from "../../../hooks/get-compliant/vendors/useUpdateVendor";
import { Routes } from "../../../root/routes";
import { GetCompliantContainer } from "../GetCompliantContainer";
import { GetCompliantFooter } from "../ProgressFooter";
import { DataRecipientProcessingRegionsSetting } from "../../../components/organisms/data-recipients/DataRecipientProcessingRegionsSetting";
import clsx from "clsx";
import { DataRecipientLogo } from "../../../components/DataRecipientLogo";
import { ZeroStateHero } from "../../../components/organisms/zerostate/ZeroStateHero";
import { toTitleCase } from "../../../util/text";
import { usePatchDataRecipients } from "../../../hooks/useDataRecipients";
import { useSnackBar } from "@truevault/aquilo";

// skip if not GDPR or PIPEDA (already hidden from sidebar, but in case user gets here directly somehow). Generally this
// kind of pattern should be enforced by the router, not sure that we need this in the page components themselves, long-
// term...
const useRequireGdprOrPipeda = (nextUrl: string) => {
  const [org] = usePrimaryOrganization();
  const history = useHistory();
  useEffect(() => {
    if (org.featureGdpr === false && org.featurePipeda === false) {
      history.push(nextUrl);
    }
  }, [history, nextUrl, org.featureGdpr, org.featurePipeda]);
};

export const ProcessingRegion: React.FC = () => {
  // Variables
  const [org] = usePrimaryOrganization();
  const [recipients, recipientsRequest, refreshRecipients] = useOrganizationVendors(
    org.id,
    undefined,
    true,
    undefined,
    ["CONSUMER"],
  );

  // State
  const [readyToRender, setReadyToRender] = useState(false);
  const [pleaseWaitMessage, setPleaseWaitMessage] = useState(null);

  const nextUrl = Routes.getCompliant.dataRecipients.sharing;
  useRequireGdprOrPipeda(nextUrl);

  useEffect(() => {
    if (NetworkRequest.areFinished(recipientsRequest)) {
      setReadyToRender(true);
    }
  }, [recipientsRequest]);

  const { updateVendor, updateVendorRequest } = useUpdateVendor(org.id, recipients);
  const patchDataRecipients = usePatchDataRecipients(org.id);

  const enqueueSnackbar = useSnackBar();

  const updateRecipientRegion = (
    recipient: OrganizationDataRecipientDto,
    regions: ProcessingRegionEnum[],
  ) => updateVendor({ vendorId: recipient.vendorId, processingRegions: regions });

  const updateAllRecipientRegions = async (region: ProcessingRegionEnum, checked: boolean) => {
    // Hide most everything until we're ready, otherwise it looks quite goofy with all the vendors switching on/off.
    setReadyToRender(false);

    const vendorIdsToUpdate = recipients
      .filter((recipient) => checked !== recipient.processingRegions.includes(region))
      .map((recipient) => recipient.vendorId);

    setPleaseWaitMessage(
      `Updating all data recipients to ${checked ? "include" : "exclude"} ${
        region === "EEA_UK" ? "EEA/UK" : toTitleCase(region.replaceAll("_", " "))
      }, this will take a few moments...`,
    );

    try {
      const result = await patchDataRecipients.mutateAsync({
        vendorIds: vendorIdsToUpdate,
        operations: [{ op: checked ? "add" : "remove", path: "/processingRegions", value: region }],
      });

      if (result.failedVendorIds.length > 0) {
        // Find recipient names for the failed vendor IDs
        const failedRecipientNames = recipients
          .filter((recipient) => result.failedVendorIds.includes(recipient.vendorId))
          .map((recipient) => recipient.name || recipient.vendorId);

        enqueueSnackbar(
          `Some recipients failed to update (${region} -> ${checked}): ${failedRecipientNames.join(
            ", ",
          )}`,
          { variant: "error", persist: true },
        );
      }
    } catch (error) {
      console.error("Failed to update recipients:", error);
      enqueueSnackbar(`Failed to update recipients: ${error.message || "Unknown error"}`, {
        variant: "error",
        persist: true,
      });
    }

    await refreshRecipients();

    setPleaseWaitMessage(null);
    setReadyToRender(true);

    // Return true if any failed, false if all succeeded
    return (patchDataRecipients.data?.failedVendorIds?.length || 0) > 0;
  };

  // If all of an org's recipients already have the region checked, check the select-all by default.
  const regionsCheckedByDefaultForUpdateAll: ProcessingRegionEnum[] = useMemo(() => {
    let regions: ProcessingRegionEnum[] = [];
    if (recipients.every((r) => r.processingRegions.includes("UNITED_STATES"))) {
      regions.push("UNITED_STATES");
    }
    if (recipients.every((r) => r.processingRegions.includes("EEA_UK"))) {
      regions.push("EEA_UK");
    }
    if (recipients.every((r) => r.processingRegions.includes("CANADA"))) {
      regions.push("CANADA");
    }
    return regions;
  }, [recipients]);

  if (!readyToRender) {
    return (
      <GetCompliantPageWrapper>
        {pleaseWaitMessage && (
          <ZeroStateHero text={pleaseWaitMessage} title={"Updating Region List"} />
        )}
        <Loading />
      </GetCompliantPageWrapper>
    );
  }

  const canNext = recipients.every((r) => r.processingRegions?.length > 0);

  const footer = (
    <ProcessingRegionFooter
      nextDisabled={!canNext}
      request={[updateVendorRequest, patchDataRecipients]}
    />
  );

  return (
    <GetCompliantPageWrapper progressFooter={footer}>
      <ProcessionRegionHeader />

      <GetCompliantContainer>
        <DataRecipientLogoProcessingRegionsSetting
          fullWidth
          updateAllRecipientRegions={updateAllRecipientRegions}
          regionsCheckedByDefaultForUpdateAll={regionsCheckedByDefaultForUpdateAll}
        />
        {recipients?.map((recipient) => (
          <DataRecipientLogoProcessingRegionsSetting
            key={recipient.id}
            recipient={recipient}
            fullWidth
            updateRecipientRegion={updateRecipientRegion}
          />
        ))}
      </GetCompliantContainer>
    </GetCompliantPageWrapper>
  );
};

type DataRecipientLogoProcessingRegionsSettingProps = {
  recipient?: OrganizationDataRecipientDto;
  fullWidth?: boolean;
  noTopBorder?: boolean;
  updateRecipientRegion?: (
    recipient: OrganizationDataRecipientDto,
    regions: ProcessingRegionEnum[],
  ) => Promise<OrganizationDataRecipientDto>;
  updateAllRecipientRegions?: (region: ProcessingRegionEnum, checked: boolean) => Promise<boolean>;
  regionsCheckedByDefaultForUpdateAll?: ProcessingRegionEnum[];
  description?: string;
};
const DataRecipientLogoProcessingRegionsSetting = ({
  recipient,
  fullWidth,
  noTopBorder,
  updateRecipientRegion,
  updateAllRecipientRegions,
  regionsCheckedByDefaultForUpdateAll,
  description,
}: DataRecipientLogoProcessingRegionsSettingProps) => (
  <div
    className={clsx(
      "data-recipient-radio-options",
      !recipient && "data-recipient-radio-options__select_all",
    )}
  >
    <div className={"data-recipient-radio-options__label"}>
      {recipient && (
        <div className="data-recipient-radio-options__logo">
          <DataRecipientLogo dataRecipient={recipient} size={24} />
        </div>
      )}
      <div className="data-recipient-radio-options__info">
        <div className="text-t3 text-weight-medium text-truncate-ellipsis">
          {recipient?.name ?? "Select/Deselect All"}
        </div>
        {description && <div className="mt-xxs text-component-help">{description}</div>}
        {!recipient && (
          <div className="mt-xxs text-component-help">Change region for all data recipients.</div>
        )}
      </div>
    </div>
    <div
      className={clsx(
        "data-recipient-radio-options__options",
        { "data-recipient-radio-options__options--full-width": fullWidth },
        { "data-recipient-radio-options__options--no-top-border": noTopBorder },
        { "data-recipient-radio-options__options--column": true },
      )}
    >
      <DataRecipientProcessingRegionsSetting
        key={recipient?.id ?? "TV_ALL"}
        recipient={recipient}
        updateState={(regions) => updateRecipientRegion(recipient, regions)}
        updateAllForRegion={(region, checked) => updateAllRecipientRegions(region, checked)}
        regionsCheckedByDefaultForUpdateAll={regionsCheckedByDefaultForUpdateAll}
      />
    </div>
  </div>
);

const ProcessionRegionHeader = () => {
  const description = (
    <>
      By default, we’ve set your Data Recipients as processing the data of consumers located in
      <strong> all compliance regions in your plan</strong>. If your Data Recipients{" "}
      <strong>do not</strong> process data of consumers located in the selected regions, unselect
      the region.
    </>
  );
  return <PageHeader titleContent="Regions" descriptionContent={description} />;
};

type FooterProps = {
  nextDisabled?: boolean;
  request?:
    | NetworkRequestState
    | NetworkRequestState[]
    | MutationOrNetworkState
    | MutationOrNetworkState[];
};
const ProcessingRegionFooter: React.FC<FooterProps> = ({ nextDisabled, request }) => {
  return (
    <GetCompliantFooter
      presentStep="DataRecipients.ProcessingRegion"
      nextDisabled={nextDisabled}
      nextDisabledTooltip="Please complete recipients before proceeding."
      saveRequests={request}
    />
  );
};
